import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, ChevronDown, Calendar, MapPin, Trash2, X } from 'lucide-react';

/**
 * Load Route Dropdown Component
 * Dropdown menu to select and load saved routes
 *
 * @param {Array} routes - Array of saved routes from database
 * @param {function} onLoad - Callback when route is loaded (routeId) => void
 * @param {function} onDelete - Callback when route is deleted (routeId) => Promise
 * @param {function} onRefresh - Callback to refresh routes list
 * @param {string} currentRouteName - Name of currently loaded route (if any)
 */
const LoadRouteDropdown = ({ routes, onLoad, onDelete, onRefresh, currentRouteName }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLoad = (route) => {
        onLoad(route.id);
        setIsOpen(false);
    };

    const handleDelete = async (e, routeId) => {
        e.stopPropagation();

        if (!confirm('Are you sure you want to delete this saved route?')) {
            return;
        }

        setDeletingId(routeId);

        try {
            await onDelete(routeId);
            onRefresh(); // Refresh the list after deletion
        } catch (error) {
            console.error('Error deleting route:', error);
            alert('Failed to delete route');
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Dropdown button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-sm"
            >
                <FolderOpen size={20} />
                <span className="hidden sm:inline">
                    {currentRouteName || 'Load Route'}
                </span>
                <span className="sm:hidden">Load</span>
                <ChevronDown
                    size={18}
                    className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800">Saved Routes</h3>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {currentRouteName && (
                            <p className="text-xs text-gray-600 mt-1">
                                Current: <span className="font-semibold">{currentRouteName}</span>
                            </p>
                        )}
                    </div>

                    {/* Routes list */}
                    <div className="overflow-y-auto flex-1">
                        {routes.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <FolderOpen size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="text-sm">No saved routes yet</p>
                                <p className="text-xs mt-1">
                                    Click "Save Route" to save your current route
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {routes.map((route) => (
                                    <div
                                        key={route.id}
                                        className="p-4 hover:bg-gray-50 transition-colors cursor-pointer group"
                                        onClick={() => handleLoad(route)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-gray-800 truncate">
                                                    {route.name}
                                                </h4>
                                                <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <MapPin size={14} />
                                                        <span>
                                                            {route.jobCount} job
                                                            {route.jobCount !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                    {route.isOptimized && (
                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                                                            Optimized
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                                    <Calendar size={12} />
                                                    <span>{formatDate(route.dateModified)}</span>
                                                </div>
                                            </div>

                                            {/* Delete button */}
                                            <button
                                                onClick={(e) => handleDelete(e, route.id)}
                                                disabled={deletingId === route.id}
                                                className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                                                title="Delete route"
                                            >
                                                {deletingId === route.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {routes.length > 0 && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                            <button
                                onClick={onRefresh}
                                className="text-sm text-green-600 hover:text-green-700 font-semibold"
                            >
                                Refresh List
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LoadRouteDropdown;
